import {
  KeyboardEvent,
  ClipboardEvent,
  ReactNode,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Button,
  Card,
  Checkbox,
  ConfigProvider,
  DatePicker,
  Flex,
  Form,
  Input,
  InputNumber,
  InputRef,
  Modal,
  Radio,
  Segmented,
  Select,
  Table,
  TableProps,
  Tooltip,
  Typography,
} from "antd";
import { useForm } from "antd/es/form/Form";
import { PlusOutlined } from "@ant-design/icons";
import { modalButtonsTheme } from "components/modals/ModalChrome";
import { showConfirmModal } from "components/modals/confirmModal";
import { pillSegmentedTheme } from "pages/analytics/RangeSegmented";
import { eyebrowStyle } from "pages/settings/rewardSettingsShared";
import { StatusBadge } from "pages/schedule/component/ScheduleStatusBadge";
import { DISCOUNT_CODE_STATUS_LABELS, DISCOUNT_CODE_STATUS_TONE } from "shared/utils";
import { getAllDiscountCodes } from "hooks/queries/discount-code";
import {
  useCreateDiscountCode,
  useUpdateDiscountCode,
  useDeleteDiscountCode,
} from "hooks/mutations/discount-code";
import { getAllCustomerByBusinessId } from "hooks/queries/customer";
import { getAllProductsByBusinessId } from "hooks/queries/product";
import {
  personFilterOption,
  renderPersonOption,
} from "shared/components/PersonSelectOption";
import { getColumnSearchProps } from "shared/components/SearchColumnProps";
import { th, tnum } from "shared/components/TableText";
import { translate } from "shared/languages";
import {
  DiscountCode,
  DiscountCodeEffectiveStatus,
  PackageProduct,
  ProductType,
} from "shared/types";
import {
  DiscountCodeEligibility,
  LoyaltyDiscountMode,
  LoyaltyDiscountScope,
} from "shared/types/enum";
import { colors } from "shared/theme/color";
import { formatCurrency } from "shared/utils";
import { DEFAULT_DATE_FORMAT, formatDate, tzDate } from "shared/utils/dayjs";
import { textPx } from "shared/constants/zoom";

const { Text } = Typography;

// Same treatment as Harga (IDR) on the package form (package-detail/index.tsx
// currencyInputProps): the InputNumber formats its OWN value as "Rp
// 1.500.000" rather than showing a raw "1500000" beside a static addon.
const currencyInputProps = {
  formatter: (value?: number | string) =>
    value ? formatCurrency(Number(value)) : "",
  parser: (value?: string) =>
    value ? parseInt(value.replace(/[Rp,.]/g, ""), 10) || 0 : 0,
};

// rc-input-number only rejects a malformed value on blur — with a custom
// formatter/parser (our Rupiah fields) or none at all (the bare percent/
// integer fields), a letter is accepted as you type and only resolved
// (usually silently to 0) once the field loses focus. Block it outright
// instead of relying on that after-the-fact cleanup.
const numericKeyGuard = (allowDecimal = false) => ({
  onKeyDown: (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.ctrlKey || e.metaKey || e.altKey) return; // copy/paste/select-all etc.
    if (e.key.length > 1) return; // Backspace, Tab, ArrowLeft, Enter, ...
    const allowed = allowDecimal ? /^[0-9.]$/ : /^[0-9]$/;
    if (!allowed.test(e.key)) e.preventDefault();
  },
  onPaste: (e: ClipboardEvent<HTMLInputElement>) => {
    const text = e.clipboardData.getData("text");
    const allowed = allowDecimal ? /^[0-9.]*$/ : /^[0-9]*$/;
    if (!allowed.test(text)) e.preventDefault();
  },
});

// Boxed, titled section — mirrors how Rezerv groups its promo-code form into
// labelled boxes ("Duration", "Limit code uses") instead of one flat list of
// inputs, so it's clear at a glance which fields belong together and what
// they're for.
const FormSection = ({
  title,
  hint,
  children,
}: {
  title: string;
  hint?: string;
  children: ReactNode;
}) => (
  <div
    style={{
      marginBottom: 16,
      padding: "14px 16px 16px",
      borderRadius: 10,
      // Border only, no fill — a tinted box read as heavy/cluttered with
      // three of them stacked in one modal. lineStrong (not the default
      // near-invisible `line`) since a white box on the white modal body
      // needs a real edge, not a hairline, to read as its own group.
      border: `1px solid ${colors.lineStrong}`,
      background: colors.card,
    }}
  >
    <Text style={eyebrowStyle}>{title}</Text>
    <div style={{ marginTop: hint ? 2 : 10, marginBottom: hint ? 10 : 0 }}>
      {hint && (
        <Text type="secondary" style={{ fontSize: textPx(12.5) }}>
          {hint}
        </Text>
      )}
    </div>
    {children}
  </div>
);

// Rezerv's "Limit code uses" checkbox-then-reveal, one Stripe-style row per
// lever (matches SettingRow in settingsRows.tsx): a plain sentence on
// the left, the field appearing flush right in the SAME row once checked —
// not stacked below with an indent, which read as a lot of empty space next
// to a narrow input. The field is never left ambiguous between "0", "not
// set" and "unlimited" the way an always-visible optional input can.
const LimitRow = ({
  checked,
  onChange,
  label,
  last,
  children,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: string;
  last?: boolean;
  children: ReactNode;
}) => (
  <Flex
    justify="space-between"
    align="center"
    gap={16}
    wrap="wrap"
    style={{
      padding: "12px 0",
      borderBottom: last ? "none" : `1px solid ${colors.line}`,
    }}
  >
    {/* Default checkbox border is too faint for an opt-in the admin must
        notice, but ink3/1.5px (the Employee Management treatment) read as
        too heavy here — one step lighter/thinner. */}
    <ConfigProvider theme={{ token: { colorBorder: colors.ink4, lineWidth: 1.2 } }}>
      <Checkbox checked={checked} onChange={(e) => onChange(e.target.checked)}>
        <Text style={{ fontSize: textPx(14) }}>{label}</Text>
      </Checkbox>
    </ConfigProvider>
    {checked && children}
  </Flex>
);

// Kelayakan Pelanggan, one radio per row instead of a collapsed Select — all
// six choices readable at a glance, matching Rezerv's "Customer eligibility"
// list. `children` (a tier/customer picker) renders indented directly below
// its own radio when that one is selected, same placement Rezerv uses for
// "Search pricing plan" under its own checkbox.
const EligibilityRow = ({
  value,
  label,
  last,
  children,
}: {
  value: string;
  label: string;
  last?: boolean;
  children?: ReactNode;
}) => (
  <div
    style={{
      padding: "10px 0",
      borderBottom: last ? "none" : `1px solid ${colors.line}`,
    }}
  >
    {/* Same lightened/thinned border as LimitRow's checkboxes above, for a
        consistent level of visibility across this modal's option rows. */}
    <ConfigProvider theme={{ token: { colorBorder: colors.ink4, lineWidth: 1.2 } }}>
      <Radio value={value}>
        <Text style={{ fontSize: textPx(14) }}>{label}</Text>
      </Radio>
    </ConfigProvider>
    {children && <div style={{ marginTop: 10, marginLeft: 24 }}>{children}</div>}
  </div>
);

// The status labels + tones live in shared/utils (DISCOUNT_CODE_STATUS_*),
// shared with Performa Kode Diskon (Analitik) and its PDF so a code reads
// the same everywhere.

// Default table order: what's usable right now, then what's queued up,
// then what's worth revisiting. Admins reuse a code by nudging its dates
// rather than creating a new one each time, so a stale created_at is a bad
// default sort — a frequently-recycled code needs to surface by CURRENT
// relevance, not by whenever it first happened to be created.
const STATUS_RANK: Record<DiscountCodeEffectiveStatus, number> = {
  active: 0,
  scheduled: 1,
  expired: 2,
  disabled: 3,
};

// Fixed table order, not a clickable column sort — Status keeps its filter
// dropdown, just not sort arrows a click could scramble this relevance
// ordering out of. Applied to the data source directly instead.
const byStatusRelevance = (a: DiscountCode, b: DiscountCode) => {
  const rankDiff = STATUS_RANK[a.effective_status] - STATUS_RANK[b.effective_status];
  if (rankDiff !== 0) return rankDiff;
  // Within Kedaluwarsa specifically: most-recently-expired first — the best
  // reuse candidates, not the ones that lapsed ages ago.
  if (a.effective_status === "expired" && a.ends_at && b.ends_at) {
    return new Date(b.ends_at).getTime() - new Date(a.ends_at).getTime();
  }
  return 0;
};

// "20%" / "Rp 50.000" — the amount half of the Aturan cell.
const ruleAmount = (c: DiscountCode) =>
  c.discount_mode === LoyaltyDiscountMode.Percent
    ? `${c.discount_value}%`
    : formatCurrency(c.discount_value);

// The scope half. Products scope shows a COUNT, not the product list itself —
// a code scoped to a dozen products used to spell every name out inline,
// wrapping the row onto two lines and making the whole table look packed
// (worse the more products a code covers). The full list is one hover away
// via the cell's tooltip instead.
const ruleScopeLabel = (c: DiscountCode) =>
  c.discount_scope === LoyaltyDiscountScope.Products
    ? `${c.products.length} ${translate("discountCodes.products_selected")}`
    : translate(`discountCodes.scope.${c.discount_scope}`);

// value/label pairs for every sellable TIER across the whole catalog —
// eligibility_package_id points at a specific tier (packages.id), one level
// deeper than Berlaku Untuk's product-level picker above. Mirrors the
// per-product tier flattening BuyPackageModal.component.tsx's tierOptions
// already does.
const buildTierOptions = (products: PackageProduct[]) =>
  products.flatMap((product) =>
    product.tiers.map((tier) => ({
      value: tier.id,
      label: `${product.name} — ${
        tier.unlimited ? translate("membership") : `${tier.total_credits}x`
      } · ${formatCurrency(tier.price)}`,
    })),
  );

// The modal's own top-level choice (Semua Pelanggan / Kriteria Tertentu /
// Pelanggan Tertentu) — shared by the modal's eligibilityGroup state AND the
// table below, so the two can't drift apart. "criteria" isn't a real
// DiscountCodeEligibility value, just the bucket for the four rule-based
// criteria nested under it in the modal.
type EligibilityGroup = "all" | "criteria" | "specific_customers";

const eligibilityGroupOf = (
  rule: DiscountCodeEligibility,
): EligibilityGroup => {
  if (rule === DiscountCodeEligibility.SpecificCustomers) {
    return "specific_customers";
  }
  if (rule !== DiscountCodeEligibility.All) return "criteria";
  return "all";
};

const eligibilityGroupLabel = (group: EligibilityGroup) =>
  translate(
    group === "criteria"
      ? "discountCodes.eligibility.criteria_group"
      : `discountCodes.eligibility.${group}`,
  );

// The Kelayakan cell shows the modal's top-level choice, matching what the
// admin actually picked there — not the more granular rule underneath it.
// The specific criterion (which one of the four, which tier, which
// customers) is one hover away via a tooltip instead of spelled out inline.
const eligibilityDetail = (
  c: DiscountCode,
  tierLabelById: Map<number, string>,
): string | null => {
  switch (c.eligibility_rule) {
    case DiscountCodeEligibility.NewCustomer:
    case DiscountCodeEligibility.ReturningCustomer:
    case DiscountCodeEligibility.NoActivePackage:
      return translate(`discountCodes.eligibility.${c.eligibility_rule}`);
    case DiscountCodeEligibility.HasActivePackage:
      return c.eligibility_package_id != null
        ? (tierLabelById.get(c.eligibility_package_id) ??
            translate("discountCodes.eligibility.has_active_package"))
        : translate("discountCodes.eligibility.has_active_package");
    case DiscountCodeEligibility.SpecificCustomers:
      return c.eligible_customers.length > 0
        ? c.eligible_customers.map((ec) => ec.name ?? ec.id).join(", ")
        : null;
    default:
      return null; // "all" — the label alone already says everything.
  }
};

const DiscountCodeFormModal = ({
  open,
  editing,
  onClose,
}: {
  open: boolean;
  editing: DiscountCode | null;
  onClose: () => void;
}) => {
  const [form] = useForm();
  const { mutateAsync: create, isPending: isCreating } = useCreateDiscountCode();
  const { mutateAsync: update, isPending: isUpdating } = useUpdateDiscountCode();
  const { mutateAsync: deleteCode, isPending: isDeleting } = useDeleteDiscountCode();
  const { data: productList } = getAllProductsByBusinessId();
  // Full business customer list, client-side filtered — same pattern
  // NewBookingModal/AddToWaitlistModal already use for a customer picker; a
  // real server search endpoint is only worth it if this ever gets slow.
  const { data: customerList } = getAllCustomerByBusinessId();

  // Checkbox state for the four Batas Penggunaan levers, independent of the
  // form store: DiscountCodesCard keys this component on an open-counter (see
  // DiscountCodesCard's modalKey), so it's FULLY remounted fresh on every
  // open — seeding from `editing` here is enough, no effect needed to resync
  // later. (Modal's own destroyOnClose only affects the Form INSIDE it, not
  // this wrapping component or its useForm() instance — relying on that
  // alone previously let a stale form store leak into the next open.)
  const [showMaxRedemptions, setShowMaxRedemptions] = useState(
    !!editing?.max_redemptions,
  );
  const [showMaxPerCustomer, setShowMaxPerCustomer] = useState(
    !!editing?.max_per_customer,
  );
  const [showMinPurchase, setShowMinPurchase] = useState(
    !!editing?.min_purchase_amount,
  );
  const [showMaxDiscount, setShowMaxDiscount] = useState(
    !!editing?.max_discount_amount,
  );

  // Maksimum Diskon only means anything for a percentage discount (a fixed
  // discount is already an absolute amount), so its row is conditional on
  // this rather than always shown.
  const discountMode = Form.useWatch("discount_mode", form);
  const isPercentMode = discountMode === LoyaltyDiscountMode.Percent;
  const eligibilityRule = Form.useWatch("eligibility_rule", form);
  const endsAtValue = Form.useWatch("ends_at", form);

  // WHO may redeem is shown as two tiers, matching Rezerv's grouping — a top
  // radio (Semua Pelanggan / Kriteria Tertentu / Pelanggan Tertentu), then
  // the four rule-based criteria nested under "Kriteria Tertentu". This
  // grouping is UI-only: "criteria" isn't a real DiscountCodeEligibility
  // value, just a bucket, so it needs its own state rather than being
  // derived from eligibility_rule (which briefly has NO value while this
  // group is selected but no criterion has been picked yet).
  const [eligibilityGroup, setEligibilityGroup] = useState<EligibilityGroup>(
    () => (editing ? eligibilityGroupOf(editing.eligibility_rule) : "all"),
  );

  // Whole catalog products (never tiers), Paket group first then Membership —
  // the same split the loyalty/referral scope pickers use.
  const productOptions = useMemo(() => {
    const products = productList?.products ?? [];
    const toOption = (p: { id: string; name: string }) => ({
      value: p.id,
      label: p.name,
    });
    const credit = products
      .filter((p) => p.product_type === ProductType.Credit)
      .map(toOption);
    const memberships = products
      .filter((p) => p.product_type === ProductType.Membership)
      .map(toOption);
    const groups = [];
    if (credit.length > 0) {
      groups.push({ label: translate("package"), options: credit });
    }
    if (memberships.length > 0) {
      groups.push({ label: translate("membership"), options: memberships });
    }
    return groups;
  }, [productList]);

  const tierOptions = useMemo(
    () => buildTierOptions(productList?.products ?? []),
    [productList],
  );

  const customerOptions = useMemo(
    () =>
      (customerList ?? []).map((c) => ({
        value: c.id,
        label: c.full_name,
        phone: c.phone_number,
      })),
    [customerList],
  );

  const initialValues = editing
    ? {
        ...editing,
        starts_at: tzDate(editing.starts_at),
        ends_at: editing.ends_at ? tzDate(editing.ends_at) : null,
      }
    : {
        discount_mode: LoyaltyDiscountMode.Percent,
        discount_scope: LoyaltyDiscountScope.All,
        eligibility_rule: DiscountCodeEligibility.All,
        status: "active",
        starts_at: tzDate(new Date()),
      };

  // Synchronous guard shared by Simpan Perubahan and Aktifkan/Nonaktifkan
  // (both hit the same update endpoint) — isCreating/isUpdating only flip
  // AFTER React re-renders the Button as disabled, leaving a brief window
  // where a fast double-click can queue two requests before that happens. A
  // ref set synchronously at the top of each handler closes that window.
  const isSubmittingRef = useRef(false);

  const onSubmit = () => {
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    form
      .validateFields()
      .then(async (values) => {
        const payload = {
          ...values,
          // Match the server's own trim+uppercase normalization so what's
          // actually submitted can never silently differ from what's shown.
          code: values.code.trim().toUpperCase(),
          // Calendar dates, not instants: the server stores them as the
          // first and last moment of those Jakarta days, so the code works
          // through the whole of its last day whatever the browser's clock.
          starts_at: values.starts_at?.format("YYYY-MM-DD"),
          ends_at: values.ends_at ? values.ends_at.format("YYYY-MM-DD") : null,
          // Only meaningful under the products scope; cleared otherwise so a
          // stale list can't linger after switching scope.
          product_ids:
            values.discount_scope === LoyaltyDiscountScope.Products
              ? (values.product_ids ?? [])
              : [],
          // Same idea for the eligibility rule's two rule-specific fields.
          eligibility_package_id:
            values.eligibility_rule === DiscountCodeEligibility.HasActivePackage
              ? values.eligibility_package_id
              : null,
          eligible_customer_ids:
            values.eligibility_rule === DiscountCodeEligibility.SpecificCustomers
              ? (values.eligible_customer_ids ?? [])
              : [],
        };
        if (editing) {
          await update({ id: editing.id, ...payload });
        } else {
          await create(payload);
        }
        onClose();
        form.resetFields();
      })
      .catch(() => {
        // AntD already paints the inline field errors — bail without the
        // unhandled rejection that would surface as a full-page overlay.
      })
      .finally(() => {
        isSubmittingRef.current = false;
      });
  };

  // Moved here from the table's row actions column (now removed — the row
  // itself opens this modal). Reuses the SAME update mutation as Simpan
  // Perubahan, acting on `editing`'s currently-SAVED values, not whatever's
  // been typed into the form but not yet submitted — a quick status flip is
  // a distinct action from saving edits, matching how it worked at the row
  // level before.
  const handleToggleStatus = async () => {
    if (!editing || isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    try {
      await update({
        id: editing.id,
        code: editing.code,
        discount_mode: editing.discount_mode,
        discount_value: editing.discount_value,
        discount_scope: editing.discount_scope,
        product_ids: editing.product_ids,
        min_purchase_amount: editing.min_purchase_amount,
        max_discount_amount: editing.max_discount_amount,
        max_redemptions: editing.max_redemptions,
        max_per_customer: editing.max_per_customer,
        starts_at: editing.starts_at,
        ends_at: editing.ends_at,
        status: editing.status === "active" ? "disabled" : "active",
        eligibility_rule: editing.eligibility_rule,
        eligibility_package_id: editing.eligibility_package_id,
        eligible_customer_ids: editing.eligible_customer_ids,
      });
      onClose();
    } finally {
      isSubmittingRef.current = false;
    }
  };

  // Gated in the footer to redemption_count === 0 — a used code must be
  // disabled (Nonaktifkan), not deleted, since discount_code_redemption rows
  // reference discount_code_id for reporting (Pembelian Paket's Diskon
  // column). This covers the "created by mistake, nobody's touched it yet"
  // case; the server re-checks the same thing and returns "has_redemptions"
  // as defense-in-depth.
  const handleDelete = () => {
    // Same isSubmittingRef guard as onSubmit/handleToggleStatus — without
    // it, a fast double-click on Hapus could open two confirm dialogs
    // before either one's onConfirm has a chance to run.
    if (!editing || isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    showConfirmModal({
      tone: "error",
      destructive: true,
      title: translate("discountCodes.delete_confirm_title"),
      content: translate("discountCodes.delete_confirm_body"),
      confirmLabel: translate("discountCodes.delete_button"),
      onConfirm: async () => {
        try {
          await deleteCode(editing.id);
          onClose();
        } finally {
          isSubmittingRef.current = false;
        }
      },
      // Backing out (Batal / the X / clicking outside) must release the
      // guard too, or a dismissed dialog would silently lock out every
      // other action in this modal for the rest of the session.
      onDismiss: () => {
        isSubmittingRef.current = false;
      },
    });
  };

  return (
    <Modal
      title={translate(
        editing ? "discountCodes.edit_title" : "discountCodes.add_title",
      )}
      open={open}
      destroyOnClose
      width={860}
      onCancel={() => {
        onClose();
        form.resetFields();
      }}
      footer={
        <ConfigProvider theme={modalButtonsTheme}>
          {/* space-between needs a left-side child to push Batal/Simpan
              right even when there's nothing to toggle (a new code has no
              saved status yet) — the empty span is that placeholder. */}
          <Flex justify="space-between" align="center">
            {editing ? (
              <Flex gap={8}>
                <Button loading={isUpdating} onClick={handleToggleStatus}>
                  {translate(
                    editing.status === "active"
                      ? "discountCodes.disable"
                      : "discountCodes.enable",
                  )}
                </Button>
                {/* Never redeemed only — a used code's history belongs in
                    reporting, so Nonaktifkan is the only tool for it. */}
                {editing.redemption_count === 0 && (
                  <Button danger loading={isDeleting} onClick={handleDelete}>
                    {translate("discountCodes.delete_button")}
                  </Button>
                )}
              </Flex>
            ) : (
              <span />
            )}
            <Flex gap={8}>
              <Button onClick={onClose}>{translate("button.cancel")}</Button>
              <Button
                type="primary"
                loading={isCreating || isUpdating}
                onClick={onSubmit}
              >
                {translate("button.save_changes")}
              </Button>
            </Flex>
          </Flex>
        </ConfigProvider>
      }
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={initialValues}
        // A little breathing room below the title — AntD's default header→
        // body gap read as cramped once "Kode" (a required field, red
        // asterisk) sat right under it.
        style={{ marginTop: 8 }}
        // This form scrolls — Kode sits at the very top, Kelayakan
        // Pelanggan at the bottom. Without this, submitting while scrolled
        // down with an empty Kode fails validation invisibly: the inline
        // error paints above the fold, and clicking Simpan Perubahan looks
        // like it silently did nothing.
        scrollToFirstError
      >
        <Form.Item
          name="code"
          label={translate("discountCodes.code_label")}
          // Uppercases the actual stored value as typed (not just a CSS
          // textTransform, which only changes how it LOOKS) — matches how
          // the server normalizes and stores it, so what the admin types is
          // exactly what saves and what the customer later enters.
          normalize={(value: string) => value?.toUpperCase()}
          rules={[
            { required: true, message: translate("form.error.required") },
            {
              pattern: /^[A-Za-z0-9][A-Za-z0-9-]*$/,
              message: translate("discountCodes.code_format"),
            },
          ]}
          // Past invoices and Pembelian read the code text live, so a used
          // code keeps its text (System Review R16); the server refuses too.
          extra={
            editing && editing.redemption_count > 0
              ? translate("discountCodes.code_locked_hint")
              : undefined
          }
        >
          <Input
            placeholder="NEWYEAR20"
            disabled={!!editing && editing.redemption_count > 0}
          />
        </Form.Item>

        <FormSection title={translate("discountCodes.section.rule")}>
          <Flex gap={16} wrap="wrap" align="flex-start">
            {/* ConfigProvider must wrap the Form.Item, not sit INSIDE it —
                Form.Item clones its direct child to inject value/onChange,
                and ConfigProvider (a plain context provider) silently drops
                both. With it inside, Segmented never received the current
                discount_mode and clicking it never actually updated the
                form's value (only the unrelated manual onChange below fired,
                so the toggle looked like it worked while isPercentMode
                stayed stuck on whatever it started as). */}
            <ConfigProvider theme={pillSegmentedTheme}>
              <Form.Item
                name="discount_mode"
                label={translate("discountCodes.mode_label")}
                rules={[{ required: true, message: translate("form.error.required") }]}
              >
                <Segmented
                  // A value valid for one mode can be nonsense for the other
                  // (Rp 50.000 read as a percentage), so switching mode
                  // clears it rather than leaving a value the admin didn't
                  // choose.
                  onChange={() =>
                    form.setFieldValue("discount_value", undefined)
                  }
                  options={[
                    {
                      value: LoyaltyDiscountMode.Percent,
                      label: translate("discountCodes.mode.percent"),
                    },
                    {
                      value: LoyaltyDiscountMode.Fixed,
                      label: translate("discountCodes.mode.fixed"),
                    },
                  ]}
                />
              </Form.Item>
            </ConfigProvider>
            <Form.Item
              name="discount_value"
              label={translate("discountCodes.value_label")}
              rules={[
                {
                  required: true,
                  // Percent allows up to 2 decimals (12.5%); fixed is whole
                  // rupiah, so only it needs the integer rule.
                  type: isPercentMode ? "number" : "integer",
                  min: isPercentMode ? 0.01 : 1,
                  max: isPercentMode ? 100 : undefined,
                  message: translate("form.error.required"),
                },
              ]}
              style={{ flex: 1, minWidth: 150 }}
            >
              {isPercentMode ? (
                <InputNumber
                  min={0.01}
                  max={100}
                  precision={2}
                  step={0.01}
                  controls={false}
                  style={{ width: "100%" }}
                  addonAfter="%"
                  {...numericKeyGuard(true)}
                />
              ) : (
                <InputNumber
                  min={1}
                  controls={false}
                  style={{ width: "100%" }}
                  {...currencyInputProps}
                  {...numericKeyGuard(false)}
                />
              )}
            </Form.Item>
            <Form.Item
              name="discount_scope"
              label={translate("discountCodes.scope_label")}
              rules={[{ required: true, message: translate("form.error.required") }]}
              style={{ flex: 1, minWidth: 180 }}
            >
              <Select
                options={[
                  {
                    value: LoyaltyDiscountScope.All,
                    label: translate("discountCodes.scope.all"),
                  },
                  {
                    value: LoyaltyDiscountScope.Packages,
                    label: translate("discountCodes.scope.packages"),
                  },
                  {
                    value: LoyaltyDiscountScope.Memberships,
                    label: translate("discountCodes.scope.memberships"),
                  },
                  {
                    value: LoyaltyDiscountScope.Products,
                    label: translate("discountCodes.scope.products"),
                  },
                ]}
              />
            </Form.Item>
          </Flex>

          <Form.Item noStyle shouldUpdate>
            {({ getFieldValue }) =>
              getFieldValue("discount_scope") ===
              LoyaltyDiscountScope.Products ? (
                <Form.Item
                  name="product_ids"
                  label={translate("discountCodes.products_label")}
                  rules={[
                    {
                      required: true,
                      message: translate("form.error.required"),
                    },
                  ]}
                  // Last field in the section: no bottom margin so the box's
                  // own padding closes the gap instead of stacking with it.
                  style={{ marginBottom: 0 }}
                >
                  <Select mode="multiple" options={productOptions} allowClear />
                </Form.Item>
              ) : null
            }
          </Form.Item>
        </FormSection>

        <FormSection title={translate("discountCodes.section.period")}>
          <Flex gap={16} wrap="wrap">
            <Form.Item
              name="starts_at"
              label={translate("discountCodes.starts_at")}
              rules={[
                { required: true, message: translate("form.error.required") },
              ]}
              style={{ flex: 1, minWidth: 180 }}
            >
              <DatePicker format={DEFAULT_DATE_FORMAT} style={{ width: "100%" }} />
            </Form.Item>
            <div style={{ flex: 1, minWidth: 180 }}>
              <Form.Item
                name="ends_at"
                label={translate("discountCodes.ends_at")}
                tooltip={translate("discountCodes.ends_at_hint")}
                style={{ marginBottom: endsAtValue ? 4 : 24 }}
              >
                {/* allowClear off — the native "x" clears the input but the
                    calendar panel keeps its last-viewed date primed
                    underneath, and clicking away to close the panel could
                    silently re-apply it. "Hapus tanggal berakhir" below is
                    the only clear path now: it writes null straight to the
                    form with no panel involved. */}
                <DatePicker
                  format={DEFAULT_DATE_FORMAT}
                  style={{ width: "100%" }}
                  allowClear={false}
                />
              </Form.Item>
              {/* Explicit clear action, not just relying on DatePicker's own
                  hover "x" — once a date's been set once, that "x" is easy to
                  miss (small, only on hover), and there's otherwise no
                  obvious way back to "no end date" at all. */}
              {endsAtValue && (
                <Button
                  type="link"
                  size="small"
                  style={{ padding: 0, height: "auto" }}
                  onClick={() => form.setFieldValue("ends_at", null)}
                >
                  {translate("discountCodes.clear_ends_at")}
                </Button>
              )}
            </div>
          </Flex>
        </FormSection>

        {/* Rezerv's "Limit code uses" box, copied structurally: a plain
            sentence per lever, unchecked by default, with the actual number
            field appearing only once its checkbox is on. Unchecking clears
            the underlying value so "unlimited" is the field's real state,
            not just a hidden one. */}
        <FormSection title={translate("discountCodes.section.limits")}>
          <LimitRow
            checked={showMaxRedemptions}
            onChange={(checked) => {
              setShowMaxRedemptions(checked);
              if (!checked) form.setFieldValue("max_redemptions", undefined);
            }}
            label={translate("discountCodes.limit_toggle.max_redemptions")}
          >
            <Form.Item
              name="max_redemptions"
              noStyle
              rules={[
                {
                  required: showMaxRedemptions,
                  message: translate("form.error.required"),
                },
              ]}
            >
              <InputNumber
                min={1}
                precision={0}
                style={{ width: 200 }}
                autoFocus
                {...numericKeyGuard(false)}
              />
            </Form.Item>
          </LimitRow>

          <LimitRow
            checked={showMaxPerCustomer}
            onChange={(checked) => {
              setShowMaxPerCustomer(checked);
              if (!checked) form.setFieldValue("max_per_customer", undefined);
            }}
            label={translate("discountCodes.limit_toggle.max_per_customer")}
          >
            <Form.Item
              name="max_per_customer"
              noStyle
              rules={[
                {
                  required: showMaxPerCustomer,
                  message: translate("form.error.required"),
                },
              ]}
            >
              <InputNumber
                min={1}
                precision={0}
                style={{ width: 200 }}
                autoFocus
                {...numericKeyGuard(false)}
              />
            </Form.Item>
          </LimitRow>

          <LimitRow
            checked={showMinPurchase}
            onChange={(checked) => {
              setShowMinPurchase(checked);
              if (!checked)
                form.setFieldValue("min_purchase_amount", undefined);
            }}
            label={translate("discountCodes.limit_toggle.min_purchase")}
            last={!isPercentMode}
          >
            <Form.Item
              name="min_purchase_amount"
              noStyle
              rules={[
                {
                  required: showMinPurchase,
                  message: translate("form.error.required"),
                },
              ]}
            >
              <InputNumber
                min={0}
                style={{ width: 200 }}
                autoFocus
                {...currencyInputProps}
                {...numericKeyGuard(false)}
              />
            </Form.Item>
          </LimitRow>

          {/* Meaningless for a fixed-amount discount (already an absolute
              value), so the whole row disappears in that mode rather than
              offering a checkbox that does nothing. */}
          {isPercentMode && (
            <LimitRow
              checked={showMaxDiscount}
              onChange={(checked) => {
                setShowMaxDiscount(checked);
                if (!checked)
                  form.setFieldValue("max_discount_amount", undefined);
              }}
              label={translate("discountCodes.limit_toggle.max_discount")}
              last
            >
              <Form.Item
                name="max_discount_amount"
                noStyle
                rules={[
                  {
                    required: showMaxDiscount,
                    message: translate("form.error.required"),
                  },
                ]}
              >
                <InputNumber
                  min={1}
                  style={{ width: 200 }}
                  autoFocus
                  {...currencyInputProps}
                  {...numericKeyGuard(false)}
                />
              </Form.Item>
            </LimitRow>
          )}
        </FormSection>

        {/* WHO may redeem, separate from Berlaku Untuk above (WHAT it
            applies to). Two tiers, matching Rezerv's own grouping: a top
            radio (Semua Pelanggan / Kriteria Tertentu / Pelanggan Tertentu),
            then the four rule-based criteria nested under "Kriteria
            Tertentu" — still mutually exclusive underneath (one
            eligibility_rule per code), just visually grouped the way
            Rezerv's screen is. */}
        <FormSection title={translate("discountCodes.section.eligibility")}>
          <Radio.Group
            style={{ width: "100%" }}
            value={eligibilityGroup}
            onChange={(e) => {
              const group = e.target.value as typeof eligibilityGroup;
              setEligibilityGroup(group);
              form.setFieldValue("eligibility_package_id", undefined);
              form.setFieldValue("eligible_customer_ids", undefined);
              if (group === "all") {
                form.setFieldValue("eligibility_rule", DiscountCodeEligibility.All);
              } else if (group === "specific_customers") {
                form.setFieldValue(
                  "eligibility_rule",
                  DiscountCodeEligibility.SpecificCustomers,
                );
              } else {
                // "criteria" has no single value of its own — cleared until
                // the admin picks one of the four nested options below.
                form.setFieldValue("eligibility_rule", undefined);
              }
            }}
          >
            <EligibilityRow value="all" label={translate("discountCodes.eligibility.all")} />

            <EligibilityRow
              value="criteria"
              label={translate("discountCodes.eligibility.criteria_group")}
            >
              {/* The required rule below already blocks Simpan Perubahan
                  correctly when nothing's picked — noStyle just means it does
                  so with no visible sign why, since a Radio.Group has no
                  obvious "error" look the way a bordered Input does. The
                  shouldUpdate block further down makes that already-enforced
                  rule visible instead of silent. */}
              {eligibilityGroup === "criteria" && (
                <Form.Item
                  name="eligibility_rule"
                  noStyle
                  rules={[
                    { required: true, message: translate("form.error.required") },
                  ]}
                >
                  <Radio.Group
                    style={{ width: "100%" }}
                    onChange={() => {
                      form.setFieldValue("eligibility_package_id", undefined);
                    }}
                  >
                    <EligibilityRow
                      value={DiscountCodeEligibility.NewCustomer}
                      label={translate("discountCodes.eligibility.new_customer")}
                    />
                    <EligibilityRow
                      value={DiscountCodeEligibility.ReturningCustomer}
                      label={translate("discountCodes.eligibility.returning_customer")}
                    />
                    <EligibilityRow
                      value={DiscountCodeEligibility.NoActivePackage}
                      label={translate("discountCodes.eligibility.no_active_package")}
                    />
                    <EligibilityRow
                      value={DiscountCodeEligibility.HasActivePackage}
                      label={translate("discountCodes.eligibility.has_active_package")}
                      last
                    >
                      {eligibilityRule === DiscountCodeEligibility.HasActivePackage && (
                        <Form.Item
                          name="eligibility_package_id"
                          noStyle
                          rules={[
                            {
                              required: true,
                              message: translate("form.error.required"),
                            },
                          ]}
                        >
                          <Select
                            showSearch
                            optionFilterProp="label"
                            placeholder={translate(
                              "discountCodes.eligibility_package_label",
                            )}
                            style={{ width: "100%" }}
                            options={tierOptions}
                          />
                        </Form.Item>
                      )}
                    </EligibilityRow>
                  </Radio.Group>
                </Form.Item>
              )}
              {eligibilityGroup === "criteria" && (
                <Form.Item noStyle shouldUpdate>
                  {() => {
                    const errors = form.getFieldError("eligibility_rule");
                    return errors.length > 0 ? (
                      <Text
                        type="danger"
                        style={{ fontSize: textPx(12.5), display: "block", marginTop: 4 }}
                      >
                        {errors[0]}
                      </Text>
                    ) : null;
                  }}
                </Form.Item>
              )}
            </EligibilityRow>

            <EligibilityRow
              value="specific_customers"
              label={translate("discountCodes.eligibility.specific_customers")}
              last
            >
              {eligibilityGroup === "specific_customers" && (
                <Form.Item
                  name="eligible_customer_ids"
                  noStyle
                  rules={[
                    { required: true, message: translate("form.error.required") },
                  ]}
                >
                  <Select
                    mode="multiple"
                    showSearch
                    allowClear
                    placeholder={translate("discountCodes.eligibility_customers_label")}
                    style={{ width: "100%" }}
                    filterOption={personFilterOption}
                    optionRender={renderPersonOption}
                    options={customerOptions}
                  />
                </Form.Item>
              )}
            </EligibilityRow>
          </Radio.Group>

          {/* validateFields() only returns values for CURRENTLY MOUNTED
              Form.Items — rc-field-form's getFieldsValue(no args) filters by
              registered field entities, not the raw store, so a value set
              purely via form.setFieldValue with no backing Form.Item is
              invisible to it. The "all"/"specific_customers" branches above
              set eligibility_rule that way (no Form.Item of their own), so
              it silently dropped out of every submit from those two
              branches — this hidden carrier keeps SOME Form.Item registered
              for "eligibility_rule" whenever the real nested one (inside
              Kriteria Tertentu, below) isn't mounted, so the value is always
              visible to validateFields() no matter which top choice is
              active. */}
          {eligibilityGroup !== "criteria" && (
            <Form.Item name="eligibility_rule" noStyle>
              <Input type="hidden" />
            </Form.Item>
          )}
        </FormSection>
      </Form>
    </Modal>
  );
};

// Uses that still count (voided sales excluded). Falls back to the raw count
// for a server that predates used_count, so the deploy order never matters.
const usedCount = (c: DiscountCode) => c.used_count ?? c.redemption_count;

// readOnly: Pembatasan Admin. DisabledContext (from the page's
// ReadOnlyWhenRestricted) already covers every Button, but the row click that
// opens Ubah Kode Diskon is a Table onRow handler, not a control, and the add
// button in the card header is better absent than greyed.
const DiscountCodesCard = ({ readOnly = false }: { readOnly?: boolean }) => {
  const { data: codes = [], isLoading } = getAllDiscountCodes();
  const sortedCodes = useMemo(
    () => [...codes].sort(byStatusRelevance),
    [codes],
  );
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<DiscountCode | null>(null);
  // DiscountCodeFormModal itself never unmounts (it's always in the tree —
  // only the AntD <Modal>'s CHILDREN unmount/remount via destroyOnClose), so
  // its useForm() instance and local useState (eligibilityGroup, the 4 limit
  // checkboxes) are NOT reset just by closing and reopening. That let the
  // form's rc-field-form STORE hang onto values from a stale render (e.g.
  // Simpan Perubahan's resetFields() closing over the pre-save
  // initialValues, since editing hadn't updated to the post-save record
  // yet) — so eligible_customer_ids/product_ids showed the PREVIOUS save's
  // list on the very next reopen, only self-correcting a reopen later. A
  // key that changes on every open forces React to fully unmount + remount
  // DiscountCodeFormModal (fresh useForm() store, fresh local state) each
  // time, so there is never anything stale left over to display.
  const [modalKey, setModalKey] = useState(0);

  // Same queryKey as the modal's fetch (getAllProductsByBusinessId with no
  // params), so React Query dedupes this to one shared request/cache entry —
  // not a second network call. Needed here to show the TIER NAME (not just
  // its numeric id) for a has_active_package code's Kelayakan cell.
  const { data: productList } = getAllProductsByBusinessId();
  const tierLabelById = useMemo(
    () =>
      new Map(
        buildTierOptions(productList?.products ?? []).map((o) => [
          o.value,
          o.label,
        ]),
      ),
    [productList],
  );

  const openAdd = () => {
    setEditing(null);
    setModalKey((k) => k + 1);
    setModalOpen(true);
  };
  const openEdit = (c: DiscountCode) => {
    setEditing(c);
    setModalKey((k) => k + 1);
    setModalOpen(true);
  };

  const codeSearchInput = useRef<InputRef>(null);

  const columns: TableProps<DiscountCode>["columns"] = [
    {
      title: th(translate("discountCodes.code_label")),
      dataIndex: "code",
      render: (code: string) => (
        <span style={{ ...tnum, fontWeight: 600 }}>{code}</span>
      ),
      sorter: (a, b) => a.code.localeCompare(b.code),
      ...getColumnSearchProps<DiscountCode, "code">({
        config: "code",
        searchInput: codeSearchInput,
      }),
    },
    {
      // Was one combined "10% · Semua produk" cell — split so each fact
      // gets its own column, matching Rezerv's Terms/Type split. Not
      // sortable — percent and Rupiah values aren't comparable on one axis —
      // but filterable by mode, which IS a plain category.
      title: th(translate("discountCodes.discount_label")),
      key: "discount",
      render: (_, c) => (
        <span style={{ ...tnum, fontSize: textPx(13) }}>{ruleAmount(c)}</span>
      ),
      filters: [
        {
          text: translate("discountCodes.mode.percent"),
          value: LoyaltyDiscountMode.Percent,
        },
        {
          text: translate("discountCodes.mode.fixed"),
          value: LoyaltyDiscountMode.Fixed,
        },
      ],
      onFilter: (value, c) => c.discount_mode === value,
    },
    {
      title: th(translate("discountCodes.scope_label")),
      key: "scope",
      filters: [
        {
          text: translate("discountCodes.scope.all"),
          value: LoyaltyDiscountScope.All,
        },
        {
          text: translate("discountCodes.scope.packages"),
          value: LoyaltyDiscountScope.Packages,
        },
        {
          text: translate("discountCodes.scope.memberships"),
          value: LoyaltyDiscountScope.Memberships,
        },
        {
          text: translate("discountCodes.scope.products"),
          value: LoyaltyDiscountScope.Products,
        },
      ],
      onFilter: (value, c) => c.discount_scope === value,
      render: (_, c) => {
        const cell = (
          <Text
            style={{
              fontSize: textPx(13),
              display: "block",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {ruleScopeLabel(c)}
          </Text>
        );
        // Only Products scope can grow unboundedly long — everything else
        // is already a short fixed label, so only that case needs a
        // tooltip revealing what got truncated/counted.
        return c.discount_scope === LoyaltyDiscountScope.Products ? (
          <Tooltip title={c.products.map((p) => p.name).join(", ")}>
            {cell}
          </Tooltip>
        ) : (
          cell
        );
      },
    },
    {
      // Same 3 choices the modal's own top-level radio offers (Semua
      // Pelanggan / Kriteria Tertentu / Pelanggan Tertentu) — filtering by
      // "Kriteria Tertentu" matches any of the four nested criteria, not one
      // specific rule.
      title: th(translate("discountCodes.eligibility_label")),
      key: "eligibility",
      filters: (["all", "criteria", "specific_customers"] as EligibilityGroup[]).map(
        (group) => ({ text: eligibilityGroupLabel(group), value: group }),
      ),
      onFilter: (value, c) => eligibilityGroupOf(c.eligibility_rule) === value,
      render: (_, c) => {
        const label = eligibilityGroupLabel(eligibilityGroupOf(c.eligibility_rule));
        const detail = eligibilityDetail(c, tierLabelById);
        const cell = (
          <Text
            style={{
              fontSize: textPx(13),
              display: "block",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {label}
          </Text>
        );
        return detail ? <Tooltip title={detail}>{cell}</Tooltip> : cell;
      },
    },
    {
      // Split like Rezerv's Start date/End date, not one combined cell —
      // an open-ended code (no ends_at) shows a plain "-" in its own
      // column instead of a bare start date with nothing after it, which
      // read as cut-off/incomplete data.
      title: th(translate("discountCodes.starts_at_column")),
      key: "starts_at",
      render: (_, c) => (
        <span style={{ ...tnum, fontSize: textPx(13) }}>{formatDate(c.starts_at)}</span>
      ),
      sorter: (a, b) =>
        new Date(a.starts_at).getTime() - new Date(b.starts_at).getTime(),
    },
    {
      title: th(translate("discountCodes.ends_at_column")),
      key: "ends_at",
      render: (_, c) => (
        <span style={{ ...tnum, fontSize: textPx(13) }}>
          {c.ends_at ? formatDate(c.ends_at) : "-"}
        </span>
      ),
      // Open-ended (no ends_at) sorts last in either direction — it's the
      // "furthest away" end date there is, not a missing/zero value.
      sorter: (a, b) => {
        const aTime = a.ends_at ? new Date(a.ends_at).getTime() : Infinity;
        const bTime = b.ends_at ? new Date(b.ends_at).getTime() : Infinity;
        return aTime - bTime;
      },
    },
    {
      title: th(translate("discountCodes.used")),
      key: "used",
      // Uses that still count: a voided sale releases the code's use, so the
      // number here matches Performa Kode Diskon and the cap the server
      // enforces. redemption_count (voided included) only gates rename/delete.
      render: (_, c) => (
        <span style={tnum}>
          {usedCount(c)}
          {c.max_redemptions ? ` / ${c.max_redemptions}` : ""}
        </span>
      ),
      sorter: (a, b) => usedCount(a) - usedCount(b),
    },
    {
      title: th(translate("label.status")),
      key: "status",
      render: (_, c) => (
        <StatusBadge
          tone={DISCOUNT_CODE_STATUS_TONE[c.effective_status]}
          label={DISCOUNT_CODE_STATUS_LABELS[c.effective_status]}
        />
      ),
      filters: (
        Object.keys(DISCOUNT_CODE_STATUS_TONE) as DiscountCodeEffectiveStatus[]
      ).map((status) => ({
        text: DISCOUNT_CODE_STATUS_LABELS[status],
        value: status,
      })),
      onFilter: (value, c) => c.effective_status === value,
    },
  ];

  return (
    <Card
      title={
        <Typography.Title level={5} style={{ margin: 0 }}>
          {translate("discountCodes.card_title")}
        </Typography.Title>
      }
      // Navy, unlike the sibling PaymentMethodsCard/LoyaltySettingsCard add
      // buttons: this page is a plain table with no other color, so the one
      // CTA carries it. type="primary" is global navy (see CLAUDE.md) — no
      // per-spot ConfigProvider needed.
      extra={
        readOnly ? undefined : (
          <Button type="primary" icon={<PlusOutlined />} onClick={openAdd}>
            {translate("discountCodes.add_button")}
          </Button>
        )
      }
    >
      <Text type="secondary" style={{ fontSize: textPx(13) }}>
        {translate("discountCodes.card_subtitle")}
      </Text>
      <Table<DiscountCode>
        showSorterTooltip={false}
        style={{ marginTop: 16 }}
        size="small"
        rowKey="id"
        loading={isLoading}
        columns={columns}
        dataSource={sortedCodes}
        pagination={false}
        locale={{ emptyText: translate("discountCodes.empty") }}
        // No more actions column — the row itself opens Ubah Kode Diskon
        // (which now also carries the Aktifkan/Nonaktifkan toggle in its
        // footer). tableLayout="fixed" with no per-column width overrides
        // splits every column evenly instead of sizing to content.
        onRow={
          readOnly
            ? undefined
            : (record) => ({
                onClick: () => openEdit(record),
                style: { cursor: "pointer" },
              })
        }
        tableLayout="fixed"
      />
      <DiscountCodeFormModal
        key={modalKey}
        open={modalOpen}
        editing={editing}
        onClose={() => setModalOpen(false)}
      />
    </Card>
  );
};

export default DiscountCodesCard;
